import { useEffect, useMemo, useState } from "react"
import { RoutePath, useRoutesHooks } from "../../../../features/routes"
import { PanelLoader, useGetCrumbs } from "../../../../features/ui"
import { operationHelpers, PaymentStatus } from "../../../operations"
import { TableName } from "../../../../tables"
import { useLocation, useNavigate } from "react-router-dom"
import { ConfirmSalePaymentForm } from "./confirmSalePaymentForm"
import { Customer, SaleOperation } from "../../state/types"
import { PaymentCategory, usePaymentTypeLoaders } from "../../../administration"
import { useCustomerLoaders } from "../../loaders/customerLoaders"
import { useDescribeHooks } from "../../../../features/data-types"
import { FiscalDocument, isUninvoicedFiscalDocumentStatus, useFiscalDocumentLoaders } from "../../../fiscal-documents"
import { PendingFinancialDelivery } from "../../services/hoardService"

export const ConfirmSalePaymentFormLoader = () => {
    const { tableName, dataType } = useRoutesHooks().parseUrl()
    const table = tableName!
    const operation = dataType! as SaleOperation

    const [customer, setCustomer] = useState<Customer>()
    const [fiscalDocuments, setFiscalDocuments] = useState<FiscalDocument[]>([])
    const [ready, setReady] = useState(false)

    const { describeCode } = operationHelpers
    const { describe } = useDescribeHooks()
    const { getRoutePath } = useRoutesHooks()
    const navigate = useNavigate()
    const { state } = useLocation()

    const paymentTypeLoaders = usePaymentTypeLoaders()
    const customerLoaders = useCustomerLoaders()
    const fiscalDocumentLoaders = useFiscalDocumentLoaders()

    const isSale = useMemo(() => table === TableName.SALE_ORDERS, [])
    // Monto de una ampliación de acopio que todavía no se guardó: se registra al confirmar el cobro
    const increaseAmount = isSale ? undefined : state?.increaseAmount as number | undefined
    // Entrega que superó el crédito del acopio: se emite recién al confirmar el cobro del excedente
    const pendingDelivery = increaseAmount ? state?.pendingDelivery as PendingFinancialDelivery | undefined : undefined
    const routePath = useMemo(() => isSale ? RoutePath.SALES : RoutePath.HOARDS, [])
    const paymentType = useMemo(() => paymentTypeLoaders().findPaymentType(operation.paymentTypeId), [operation.paymentTypeId])
    // La facturación pudo haber arrancado aunque la venta siga impaga
    const isInvoiceStarted = !isUninvoicedFiscalDocumentStatus(operation.invoiceStatus)

    const load = async () => {
        // Un acopio pagado igual se puede cobrar si se está ampliando
        const isPayed = operation.paymentStatus === PaymentStatus.PAYED && !increaseAmount
        // Un acopio en cuenta corriente solo se cobra acá cuando se cobra ahora una ampliación
        const isCreditPayment = paymentType?.category === PaymentCategory.CREDIT_ACCOUNT && !increaseAmount
        if (isCreditPayment || isPayed) {
            navigate(getRoutePath(routePath, operation.id))
        } else {
            const { loadCustomer } = customerLoaders()
            const { loadOperationFiscalDocuments } = fiscalDocumentLoaders()
            const loadedCustomer = await loadCustomer(operation.customerId)
            setCustomer(loadedCustomer)
            // Se usan para advertir si el medio elegido cambia el recargo de comprobantes ya emitidos.
            // En acopios se cargan siempre porque el estado de facturación del detalle puede venir desactualizado.
            // En ventas alcanza con pedirlos cuando la facturación ya arrancó, para no sumar un query a cada cobro.
            if (table === TableName.HOARD_ORDERS || isInvoiceStarted) {
                setFiscalDocuments(await loadOperationFiscalDocuments(operation.id))
            }
        }
    }

    useEffect(() => {
        load().then(() => setReady(true))
    }, [])
    
    const title = increaseAmount ?
        `Confirmar Pago de la Ampliación de ${describe(table, operation)}` :
        `Confirmar Pago de ${describe(table, operation)}`
    const crumbs = useGetCrumbs(
        isSale ? 'Ventas' : 'Acopios',
        routePath,
        describeCode(operation),
        operation.id,
        'Confirmar Pago'
    )

    return (
        <PanelLoader
            title={title}
            crumbs={crumbs}
            ready={ready}
        >
            <ConfirmSalePaymentForm
                table={table}
                operation={operation}
                customer={customer!}
                fiscalDocuments={fiscalDocuments}
                increaseAmount={increaseAmount}
                pendingDelivery={pendingDelivery}
            />
        </PanelLoader>
    )
}
